import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import AiAgentIcon from '@atlaskit/icon/core/ai-agent';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';

import { ANALYTICS_SOURCE, SHARE_CONTEXT_ACTION_SUBJECT_ID } from '../../analytics.ts';
import { ShareContextModalLoadable } from '../share-context-modal/ShareContextModalLoadable.tsx';
import { useTwgAnalyticsEvents } from '../../useTwgAnalyticsEvents.ts';

const i18n = defineMessages({
	shareWithAgent: {
		id: 'byline-connections.preview-panel.header.share-with-agent',
		defaultMessage: 'Share with agent',
		description:
			"Button label in the Teamwork Graph preview panel header. Opens options to share this content's context with an artificial intelligence agent.",
	},
	shareContext: {
		id: 'byline-connections.preview-panel.modal.share-context-button',
		defaultMessage: 'Share context',
		description:
			"Label for the button in the Teamwork Graph modal's header that shares this content's Teamwork Graph context — the connected work items, documents and people — with someone else.",
	},
});

// One entry per surface, so a variant is a single lookup rather than a ternary per prop.
const variants = {
	default: { appearance: 'primary', iconBefore: LinkExternalIcon, label: i18n.shareContext },
	header: { appearance: 'default', iconBefore: AiAgentIcon, label: i18n.shareWithAgent },
} as const;

export type TeamworkGraphShareContextButtonProps = {
	contentId?: string;
	testId?: string;
	/** `default` is the modal top bar's primary action; `header` is the preview panel header's. */
	variant?: 'default' | 'header';
	/**
	 * Hands modal ownership to the caller: the click still reports analytics, but this component
	 * renders no modal and calls `onOpen` instead of opening one itself. Required inside the byline
	 * popup, where a click in the modal's portal reads as an outside click and unmounts the popup —
	 * taking the button and any modal it owned with it. Omit it to keep the self-contained
	 * behaviour the panel header and the Teamwork Graph modal rely on.
	 */
	onOpen?: () => void;
	/** Overrides the event source so a caller's surface is distinguishable in analytics. */
	source?: string;
	/** Forwarded to the modal's Rovo button. Unused with `onOpen`, where the caller owns the modal. */
	onAskRovo?: (prompt: string) => void;
};

export function TeamworkGraphShareContextButton({
	contentId,
	testId = 'teamwork-graph-modal-share-context',
	variant = 'default',
	onOpen,
	source = ANALYTICS_SOURCE,
	onAskRovo,
}: TeamworkGraphShareContextButtonProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const { appearance, iconBefore, label } = variants[variant];
	const { createAnalyticsEvent } = useTwgAnalyticsEvents();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [hasOpenedModal, setHasOpenedModal] = useState(false);

	const handleClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: SHARE_CONTEXT_ACTION_SUBJECT_ID,
				source,
			},
		}).fire();
		if (onOpen) {
			onOpen();
			return;
		}
		setHasOpenedModal(true);
		setIsModalOpen(true);
	}, [createAnalyticsEvent, onOpen, source]);

	const closeModal = useCallback(() => setIsModalOpen(false), []);

	return (
		<>
			<Button appearance={appearance} iconBefore={iconBefore} onClick={handleClick} testId={testId}>
				{formatMessage(label)}
			</Button>
			{/* Keep the modal mounted so its close transition can finish. */}
			{hasOpenedModal && (
				<ShareContextModalLoadable
					isOpen={isModalOpen}
					onClose={closeModal}
					contentId={contentId}
					onAskRovo={onAskRovo}
				/>
			)}
		</>
	);
}
